import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';

import SearchIcon from '@mui/icons-material/Search';
import { visuallyHidden } from '@mui/utils';

import { Search, SearchIconWrapper, StyledInputBase } from './styles';

function TableHeader({ headCells, order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable({
  data = [],
  defaultOrderBy,
  hasSearch = false,
  headCells = [],
  onChange,
  totalRows,
}) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [search, setSearch] = useState('');

  useEffect(() => {
    onChange({
      order: 'asc',
      orderBy: defaultOrderBy,
      page: 0,
      rowsPerPage: 5,
      search: '',
    });
  }, [defaultOrderBy, onChange]);

  const onRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    onChange({
      order: isAsc ? 'desc' : 'asc',
      orderBy: property,
      page,
      rowsPerPage,
      search,
    });
  };
  const onChangePage = (event, newPage) => {
    setPage(newPage);
    onChange({
      order,
      orderBy,
      page: newPage,
      rowsPerPage,
      search,
    });
  };
  const onChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    onChange({
      order,
      orderBy,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
      search,
    });
  };
  const onSearch = (event) => {
    event.preventDefault();
    setPage(0);
    onChange({
      order,
      orderBy,
      page: 0,
      rowsPerPage,
      search,
    });
  };

  const emptyRows = Math.max(0, rowsPerPage - data.length);

  return (
    <>
      <TableContainer>
        {hasSearch && (
          <Toolbar sx={{ justifyContent: 'flex-end' }}>
            <form noValidate onSubmit={onSearch}>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search…"
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ 'aria-label': 'search' }}
                  value={search}
                />
              </Search>
            </form>
          </Toolbar>
        )}
        <Table>
          <TableHeader
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={onRequestSort}
          />
          <TableBody>
            {data.map((row, index) => {
              return (
                <TableRow key={row._id}>
                  {headCells.map(({ id, numeric, transform }) => (
                    <TableCell align={numeric ? 'right' : 'left'} key={id}>
                      {transform ? transform(row[id], row) : row[id]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 73 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
}
